<template>
	<div
		:class="{
			'brand-logo': true,
			'is-logo': !!brandImg.isLogo,
			centre: square,
			white: !!brandImg.white && useWhiteBackground,
			disabled: disabled,
			square: square,
			blur: blur,
			'is-upload': showUpload,
			'is-placeholder': !brandImg.isLogo && !showUpload,
			'is-pending': !!brandImg.pending,
			'upload-on-hover': canUploadLogo && !!brandImg.isLogo,
			'left-aligned': leftAligned,
		}"
		@click="clicked">
		<img :src="brandImg.img" :alt="brandInfo.brand_name" v-if="showLogo" :style="logoStyles" />
		<div v-if="canUploadLogo" @click="showUploader = true" class="upload-logo-container">
			<Icon type="upload" colour="#679DA2" class="upload-logo-icon" />
			<span class="upload-logo-text" v-if="!hasApprovedLogo">Upload logo</span>
			<span class="upload-logo-text" v-if="hasApprovedLogo">Replace logo</span>
		</div>

		<Uploader
			:imagesOnly="true"
			title="Upload logo"
			dragText="Drag your logo here - .svg, .jpg or .png format"
			:formData="uploaderFormData"
			@close-uploader="closeUploader"
			v-if="showUploader" />
	</div>
</template>

<script>
import { ref, computed } from "vue";
import { useVueInstance } from "../../composables/useVueInstance";
import Uploader from "./../Uploader.vue";
import Icon from "./BaseIcon.vue";
export default {
	name: "BrandLogo",
	components: {
		Uploader,
		Icon,
	},
	props: {
		brandInfo: {
			type: Object,
			required: true,
		},
		centre: {
			type: Boolean,
			default: true,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		square: {
			type: Boolean,
			default: false,
		},
		uploadAllowed: {
			type: Boolean,
			default: false,
		},
		replaceAllowed: {
			type: Boolean,
			default: true,
		},
		showPending: {
			type: Boolean,
			default: false,
		},
		logoMaxHeight: {
			type: Number,
			default: 0,
		},
		leftAligned: {
			type: Boolean,
			default: false,
		},
		useWhiteBackground: {
			type: Boolean,
			default: true,
		},
	},
	setup(props, { emit }) {
		const { $toast } = useVueInstance();

		const blur = computed(() => {
			return !!props.brandInfo.brand_logo_is_blurred;
		});

		const clicked = () => {
			emit("clicked", props.brandInfo);
		};

		const reload = () => {
			emit("reload", props.brandInfo);
		};

		const brandImg = computed(() => {
			if (props.brandInfo.brand_logo) {
				return { img: props.brandInfo.brand_logo, isLogo: true, white: props.brandInfo.brand_logo_is_white };
			}
			if (props.showPending && props.brandInfo.brand_logo_pending) {
				return { img: props.brandInfo.brand_logo_pending, isLogo: true, pending: true };
			}
			let img;
			try {
				img = require("../../assets/img/brands/" + props.brandInfo.brand_category.value[0] + ".jpg");
			} catch (err) {
				img = require("../../assets/img/brands/0.jpg");
			}
			return { img: img, isLogo: false };
		});

		const hasApprovedLogo = computed(() => !!props.brandInfo.brand_logo);

		const canUploadLogo = computed(() => {
			return props.uploadAllowed && (!hasApprovedLogo.value || props.replaceAllowed);
		});

		const logoStyles = computed(() => {
			let styles = [];
			if (props.logoMaxHeight > 0) {
				styles.push(`max-height: ${props.logoMaxHeight}px;`);
				styles.push(`width: auto;`);
			}
			return styles.join("; ");
		});

		const showLogo = computed(() => {
			return brandImg.value.isLogo || !canUploadLogo.value;
		});

		const showUpload = computed(() => {
			return !brandImg.value.isLogo && canUploadLogo.value;
		});

		const uploaderFormData = ref({
			type: "brand-image",
			vars: {
				imageType: "brand-logo",
				brandName: null,
				brandDescription: null,
			},
		});

		const showUploader = ref(false);

		const closeUploader = (response) => {
			if (response) {
				if (response.error) {
					$toast.error(response.error);
				} else {
					reload();
				}
			}
			showUploader.value = false;
		};

		return {
			clicked,
			brandImg,
			showLogo,
			showUpload,
			hasApprovedLogo,
			canUploadLogo,
			uploaderFormData,
			closeUploader,
			showUploader,
			logoStyles,
			blur,
		};
	},
};
</script>
<style lang="less" scoped>
@import "./../../assets/css/less/_settings.less";
@import "./../../assets/css/less/_helper.less";
.brand-logo {
	position: relative;
	width: 100%;
	text-align: center;

	&.blur {
		filter: blur(5px);
	}

	&.square {
		aspect-ratio: 1 / 1;
	}

	img {
		width: auto;
		max-width: 100%;
	}

	&.centre,
	&.white,
	&.is-pending {
		overflow: hidden;

		img {
			position: absolute;
			top: 0;
			left: 0;
			object-fit: contain;
		}

		&.is-logo {
			img {
				max-height: 80%;
				max-width: 80%;
				width: auto;
				height: auto;
				margin: auto;
				display: block;
				position: absolute;
				top: 50%;
				left: 50%;
				transform: translate(-50%, -50%);
			}
			&.square {
				img {
					max-height: 90%;
					max-width: 90%;
				}
			}
		}
	}

	&.white {
		background-color: @guidedRose;
		aspect-ratio: 1 / 1;
		.elevation.card();
		.rounded(50%);

		&.is-logo {
			img {
				max-height: 65%;
				max-width: 65%;
			}
		}
	}

	&.is-placeholder {
		.elevation.card();
		.rounded(50%);
		overflow: hidden;
		width: auto;

		img {
			height: 100%;
			object-fit: cover;
		}
	}

	&.disabled {
		img {
			filter: grayscale(100%);
		}
	}

	.upload-logo-container {
		text-align: center;
		cursor: pointer;

		.upload-logo-icon {
			width: 16px;
			height: 16px;
		}

		.upload-logo-text {
			color: @formDefaultColour;
			display: block;
			margin: 0;
		}
	}

	&.is-pending {
		display: flex;
		align-items: center;
		justify-content: center;
		img {
			filter: grayscale(100%);
			opacity: 0.5;
		}
		&:after {
			content: "Logo pending approval";
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			color: #fff;
			font-size: 12px;
			background-color: fade(@guidedRose, 75%);
			padding: 3px 6px;
			line-height: 1.1em;
			.rounded(3px);
			.elevation.card();
		}
	}

	&.upload-on-hover {
		cursor: pointer;
		min-height: 45px;
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;

		&.left-aligned {
			justify-content: flex-start;
		}

		img {
			max-width: 100%;
			width: auto;
			max-height: 60px;
		}

		.upload-logo-container {
			display: none;
			z-index: 2;
			position: absolute;
			top: 50%;
			left: 50%;
			width: 100%;
			transform: translate(-50%, -50%);
		}
		&:hover {
			background: none;
			.upload-logo-container {
				display: block;
			}
			img {
				opacity: 0;
			}
			&:after {
				display: none;
			}
		}

		&.is-pending {
			min-height: 60px;
		}
	}
}
</style>
